import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { fetchSelectedVenueApplication, saveSelectedVenueApplication } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { VenueSetupBackButton } from '../../components/venue/VenueSetupBackButton';
import { SettingStatusPill } from '../../components/venue/SettingStatusPill';

const font = "'Inria Serif', serif";

const DAYS = [
  { key: 'mo', label: 'Monday' },
  { key: 'tu', label: 'Tuesday' },
  { key: 'we', label: 'Wednesday' },
  { key: 'th', label: 'Thursday' },
  { key: 'fr', label: 'Friday' },
  { key: 'sa', label: 'Saturday' },
  { key: 'su', label: 'Sunday' },
];

const ALL_KEYS = DAYS.map((d) => d.key);

// Generate time options (00:00 to 23:30 in 30-min steps)
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

function TimeSelect({ value, onChange, label }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ fontFamily: font, fontSize: 15, fontWeight: 600, color: 'var(--text)', padding: '6px 4px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--surface)', outline: 'none', width: 80, textAlign: 'center' }}>
      <option value="">{label}</option>
      {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

export default function VenueOperatingHoursPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [hours, setHours] = useState({});
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const latestHoursRef = useRef({});

  useEffect(() => {
    const fetch = async () => {
      try {
        const application = await fetchSelectedVenueApplication(activeVenueAppId);
        setHours(application?.operating_hours || {});
      } catch (err) { console.error(err); }
      finally { setLoaded(true); }
    };
    fetch();
  }, [activeVenueAppId]);

  // Auto-save whenever hours change (debounced 500ms). Skips the very
  // first render after load so we don't POST an unchanged payload.
  useEffect(() => {
    latestHoursRef.current = hours;
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveSelectedVenueApplication({ operating_hours: latestHoursRef.current, step: 6, submitted: false }, activeVenueAppId);
      } catch (err) {
        toast.error('Couldn’t save hours — check your connection');
      }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [hours, loaded, activeVenueAppId]);

  const parseHours = (val) => {
    if (!val || val.toLowerCase() === 'closed') return { open: '', close: '', closed: true };
    const parts = val.split('-');
    return { open: parts[0]?.trim() || '', close: parts[1]?.trim() || '', closed: false };
  };

  const updateDay = (key, field, value) => {
    const current = parseHours(hours[key]);
    if (field === 'closed') {
      setHours(prev => ({ ...prev, [key]: value ? 'Closed' : '' }));
    } else {
      const updated = { ...current, [field]: value, closed: false };
      const newVal = updated.open && updated.close ? `${updated.open}-${updated.close}` : updated.open || updated.close || '';
      setHours(prev => ({ ...prev, [key]: newVal }));
    }
  };

  const handleSave = async () => {
    // Fire-and-forget flush of pending debounce, then defer navigation
    // to the back arrow which handles setup-mode vs settings routing.
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    try {
      await saveSelectedVenueApplication({ operating_hours: latestHoursRef.current, step: 6, submitted: false }, activeVenueAppId);
    } catch (err) { /* auto-save will retry on next change; noop */ }
  };

  // Flush any pending debounced save immediately. Used by the back
  // button so edits made in the last 500ms aren't dropped when the
  // user leaves the page.
  const flushPendingSave = async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try {
        await saveSelectedVenueApplication({ operating_hours: latestHoursRef.current, step: 6, submitted: false }, activeVenueAppId);
      } catch { /* silent — user is leaving; next visit will refetch truth */ }
    }
  };

  const copyMondayToAll = () => {
    const mondayVal = hours['mo'];
    if (!mondayVal || !mondayVal.trim()) return;
    const updated = {};
    ALL_KEYS.forEach(k => { updated[k] = mondayVal; });
    setHours(prev => ({ ...prev, ...updated }));
    toast.success('Monday hours copied to all days');
  };

  return (
    <div className="app-shell" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <VenueSetupBackButton testId="venue-hours-back" onBeforeNavigate={flushPendingSave} />
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-operating-hours-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 16 }}>Operating Hours</h2>

      {loaded ? (
        <div style={{ marginBottom: 14 }}>
          <SettingStatusPill
            complete={Object.values(hours).some((v) => typeof v === 'string' && v.trim().length > 0)}
            missingText="Set your weekly schedule"
            testId="venue-hours-status-pill"
          />
        </div>
      ) : null}

      {/* Copy Monday to all days — shows once Monday has a real time set */}
      {hours['mo'] && hours['mo'].trim() && hours['mo'].toLowerCase() !== 'closed' ? (
        <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginBottom: 14 }}>
          <button
            type="button"
            onClick={copyMondayToAll}
            data-testid="venue-hours-copy-monday"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '10px 14px',
              borderRadius: 14,
              background: 'var(--surface)',
              border: '1.5px dashed var(--accent-purple)',
              cursor: 'pointer',
              textAlign: 'left',
              boxShadow: '0px 3px 6px var(--inner-shadow)',
            }}
          >
            <span style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Copy size={16} strokeWidth={2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>Copy Monday to all days</p>
              <p style={{ margin: '2px 0 0', fontFamily: font, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.2 }}>Apply {hours['mo']} to every day of the week</p>
            </div>
          </button>
        </div>
      ) : null}

      <div data-testid="venue-hours-days" style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
        {DAYS.map(d => {
          const parsed = parseHours(hours[d.key]);
          return (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--hairline)' }}>
              <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', width: 90 }}>{d.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <TimeSelect value={parsed.open} onChange={v => updateDay(d.key, 'open', v)} label="Open" />
                <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-3)' }}>—</span>
                <TimeSelect value={parsed.close} onChange={v => updateDay(d.key, 'close', v)} label="Close" />
                <button onClick={() => updateDay(d.key, 'closed', !parsed.closed)}
                  style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: parsed.closed ? 'var(--status-danger-text)' : 'var(--text-3)', background: parsed.closed ? 'var(--status-danger-surface)' : 'var(--surface-2)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', marginLeft: 4 }}>
                  {parsed.closed ? 'Closed' : 'X'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={handleSave}
        data-testid="venue-hours-save"
        style={{ display: 'none' }}>
        Save
      </button>
    </div>
  );
}
